<template>
  <RevInputAutocomplete
    :id="fieldName"
    v-model="query"
    :aria-Describedby
    autocomplete="off"
    autocorrect="off"
    :clear-button-aria-label
    data-cs-mask
    :data-test="`${fieldName}Autocomplete`"
    :description
    :error
    :label
    listbox-placement="bottom"
    :name="fieldName"
    :options="currentOptions"
    spellcheck="false"
    type="text"
    @input="handleModelInput"
    @select-item="handleSelectItem"
  >
    <template #default="item">
      <RevListItemInteractive
        :id="item?.id"
        class="p-12"
        :index="item?.index"
        :place-id="item?.placeId"
        :role="item?.role"
        @click="eventHandlers(item).onClick"
        @mousedown="eventHandlers(item).onMousedown"
        @mousemove="eventHandlers(item).onMousemove"
      >
        <template #label>
          <span
            v-for="(itemLabel, index) in optionLabels(item)"
            :key="`${itemLabel.text}-${index}`"
            :class="itemLabel.class"
          >
            {{ itemLabel.text }}
          </span>
        </template>
      </RevListItemInteractive>
    </template>
  </RevInputAutocomplete>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, ref } from 'vue'

import type { Country } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import {
  type Option as DesignSystemOption,
  RevInputAutocomplete,
} from '@ds/components/InputAutocomplete'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'

import {
  ADDRESS_AUTOCOMPLETE_DEBOUNCE_DELAY,
  ADDRESS_AUTOCOMPLETE_MINIMUM_LENGTH,
} from '../../constants/autocomplete'
import type { BmPlacesDetails, Option } from '../../types/bmPlaces'
import { FeatureCode } from '../../types/featureCode'
import { BmPlaces as BmPlacesClient } from '../../utils/bmPlaces/bmPlaces'

export type AddressAutocompleteProps = {
  label: string
  fieldName: string
  country?: Country | MarketCountryCode | ''
  error?: string
  featureCode: FeatureCode
  autocompleteMaxItems?: number
  clearButtonAriaLabel?: string
  ariaDescribedby?: string
  description?: string
}

const props = withDefaults(defineProps<AddressAutocompleteProps>(), {
  country: '',
  autocompleteMaxItems: 5,
  clearButtonAriaLabel: undefined,
  ariaDescribedby: undefined,
  error: undefined,
  description: undefined,
})

const emit = defineEmits<{ 'select-item': [BmPlacesDetails, number] }>()

defineOptions({
  inheritAttrs: false,
})

const options = ref<{ [query: string]: Option[] }>({})
const query = defineModel<string>({ default: '' })

const logger = useLogger()

const currentCountry = useMarketplace()?.market?.countryCode
const i18nLocale = useI18nLocale()

// Used to not break IDE highlighting in the template
const optionLabels = (item: unknown): Option['labels'] => {
  return (item as Option)?.labels ?? []
}

// Used to not break IDE highlighting in the template
const eventHandlers = (item: unknown) => {
  const myItem = item as Record<string, unknown>

  return {
    onClick: myItem?.onClick as (() => void) | undefined,
    onMousedown: myItem?.onMousedown as (() => void) | undefined,
    onMousemove: myItem?.onMousemove as (() => void) | undefined,
  }
}

const searchClient = computed(
  () =>
    new BmPlacesClient({
      country: props.country || currentCountry,
      language: i18nLocale,
      httpFetch: $httpFetch,
    }),
)

export interface OptionB {
  key?: string
  label: string
}

const currentOptions = computed(() => {
  // Get at most autocompleteMaxItems options for the current search query
  const filteredOptions = options.value[query.value]
    ? options.value[query.value].slice(0, props.autocompleteMaxItems)
    : []

  // Transform our currentOptions type to match ds Option type while preserving original data
  return filteredOptions.map<DesignSystemOption>((option) => ({
    ...option,
    key: option.placeId,
    label: option.labels.map((label) => label.text).join(' '),
  }))
})

const handleModelInput = useDebounceFn(async function updateOptions() {
  try {
    if (!props.featureCode) {
      logger.error('[Address Autocomplete] FeatureCode is missing', {
        route: useRoute().name,
        owners: ['bot-squad-checkout-front', 'bot-squad-shipping-front'],
      })
    }

    const optionsFromAPI =
      query.value.length >= ADDRESS_AUTOCOMPLETE_MINIMUM_LENGTH
        ? await searchClient.value.getOptions({
            query: query.value,
            featureCode: props.featureCode,
          })
        : []

    options.value = { ...options.value, [query.value]: optionsFromAPI }
  } catch (error) {
    // Cancelled: do nothing
    // Errored: already logged using API SDK
  }
}, ADDRESS_AUTOCOMPLETE_DEBOUNCE_DELAY)

async function handleSelectItem(
  option: DesignSystemOption,
  id: string,
  index: number,
) {
  // Cast needed since we are customizing the return Option of the Autocomplete but the type signature is hardcoded
  const myOption = option as unknown as Option

  query.value = myOption.labels.map(({ text }) => text).join('')

  const selectedItem = await searchClient.value.getDetails(
    myOption,
    props.featureCode,
  )

  return emit('select-item', selectedItem, index)
}
</script>
